// Umami, our own analytics (docs/ANALYTICS.md). `track` sends a custom event. `beforeSend` is the tracker's
// `data-before-send` hook (public/index.html, issue 1599): every beacon passes through it on its way out.
//
// - An address is reported canonically: its path, plus `slug` when it names a rules entry. Filters, page, sort,
//   `?system=`, ids, campaigns and wiki pages are dropped, so a list is one page and a campaign is never named.
// - The tracker counts a page view on every URL change, and the list pages write their filters into the URL. A page
//   view whose canonical address is the one just counted is a filter change, not a page: it is not sent (the
//   list's own `filter-change` event says what changed).
// - Every beacon carries the active game system as its `tag`, so Umami splits usage by system.
// - A page title is kept only on a rules entry. A campaign's or wiki page's title is the table's own content.
import { getActiveSystem } from '../config/system';

export const track = (event, data) => {
  if (window.umami) window.umami.track(event, data);
};

/** The global the tracker's `data-before-send` names. */
export const BEFORE_SEND = 'tdtAnalyticsBeforeSend';

/** `href` as its path and, for a rules entry, its slug; anything unreadable as it came. */
export function canonicalUrl(href) {
  if (typeof href !== 'string') return href;
  let u;
  try { u = new URL(href); } catch (e) { return href; }
  const slug = u.searchParams.get('slug');
  return `${u.origin}${u.pathname}${slug ? `?slug=${encodeURIComponent(slug)}` : ''}`;
}

/** An outside referrer as its origin and path: a search page's query may say who someone is. */
function referrerOf(href, origin) {
  if (typeof href !== 'string' || !href) return href;
  let u;
  try { u = new URL(href); } catch (e) { return href; }
  return u.origin === origin ? canonicalUrl(href) : `${u.origin}${u.pathname}`;
}

let lastView = null;
/** For tests: forget the last page view. */
export const resetBeforeSend = () => { lastView = null; };

export function beforeSend(type, payload) {
  if (!payload || typeof payload !== 'object') return payload;
  const url = canonicalUrl(payload.url);
  let origin = null;
  try { origin = new URL(payload.url).origin; } catch (e) { /* the referrer is kept as it came */ }
  const isView = type === 'event' && !payload.name;
  if (isView) {
    if (url === lastView) return null;
    lastView = url;
  }
  const out = { ...payload, url, referrer: referrerOf(payload.referrer, origin), tag: String(getActiveSystem() || '').slice(0, 50) || undefined };
  if (!/[?&]slug=/.test(url || '')) delete out.title;
  return out;
}

if (typeof window !== 'undefined') {
  window[BEFORE_SEND] = beforeSend;
  // The browser installed the site as an app (issue 1600). The site has no install button of its own, so this is the one moment.
  window.addEventListener('appinstalled', () => track('install-pwa'));
}
